import React, { useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getContractsByCustomerService } from "../../services/contract/services";
import Loading from "../loading/Loading";
import Pagination from "../pagination/Pagination";
import NoResults from "../noResults/noResults";
import { useTranslator } from "../../context/translator";

const TableContract = ({ customerId }) => {
  const { translate } = useTranslator();
  const navigate = useNavigate();
  const [clientsData, setClientsData] = useState([]);
  // track page number
  const [page, setPage] = useState(1);
  // total number of records
  const [pageNumber, setPageNumber] = useState(0);
  // track API response time
  const [isLoading, setIsLoading] = useState(false);
  const [totalContracts, setTotalContracts] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [categoryText] = useState({
    dg: translate("digital"),
    ph: translate("physical"),
    sc: translate("scanned"),
    pl: "Planes",
    nsg: translate("no-signature"),
  });
  const [temporalityText] = useState({
    tmp: translate("temporary"),
    prm: translate("permanent"),
  });
  const [categoryOptions] = useState([
    {
      key: "dg",
      name: translate("digital"),
    },
    {
      key: "ph",
      name: translate("physical"),
    },
    {
      key: "sc",
      name: translate("scanned"),
    },
    {
      key: "pl",
      name: "Planes",
    },
    {
      key: "nsg",
      name: translate("no-signature"),
    },
  ]);

  const viewContract = (contractId) => {
    return navigate(`/clients/${customerId}/${contractId}`);
  };

  const handleOnChangeCategory = (event) => {
    const value = event.target.value;
    setCategory(value);
    setPage(1);
  };

  const handleOnChangeSearch = (event) => {
    const value = event.target.value;
    setSearch(value);
    setPage(1);
  };

  const handleNewContract = () => {
    return navigate(`/clients/${customerId}/new`);
  };

  const getContract = React.useCallback(async () => {
    setIsLoading(true);

    const data = await getContractsByCustomerService({
      customerId,
      page,
      search,
      contractType: category,
      pageSize: 5,
    });

    setIsLoading(false);
    setClientsData(data?.contracts ?? []);
    const count = data?.totalContracts ?? 0;
    setTotalContracts(count);
    setPageNumber(Math.ceil(count / 5));
  }, [category, customerId, page, search]);

  useEffect(() => {
    getContract();
  }, [getContract]);

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <div className="grid gap-6 mb-6 md:grid-cols-3  mt-2 mr-2 ml-2">
        <div>
          <label
            htmlFor="category"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("filter-by-category")}
          </label>
          <select
            id="category"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            defaultValue={"all"}
            onChange={handleOnChangeCategory}
          >
            <option value="all">{translate("all")}</option>
            {categoryOptions?.map((option) => (
              <option key={option.key} value={option.key}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="search"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("search")}
          </label>
          <input
            type="text"
            id="search"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder={translate("search") + "..."}
            onChange={handleOnChangeSearch}
          />
        </div>
        <div className="flex justify-end items-end">
          <button
            type="button"
            className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 dark:focus:ring-green-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
            onClick={handleNewContract}
          >
            {translate("new-contract")}
          </button>
        </div>
      </div>

      {!isLoading && clientsData.length === 0 && (
        <NoResults message="No contracts found" />
      )}
      {clientsData.length === 0 && isLoading && <Loading />}
      {clientsData.length > 0 && (
        <>
          <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                  {translate("contract-name")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {translate("date-expiration")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {translate("category")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {translate("temporality")}
                </th>
                <th scope="col" className="px-6 py-3">
                  <span className="sr-only">{translate("edit")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                clientsData?.map((client) => {
                  return (
                    <tr
                      key={client?._id}
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <th
                        scope="row"
                        className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                      >
                        {client?.contractName}
                      </th>
                      <th
                        scope="row"
                        className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                      >
                        {new Date(client?.dateExpiration).toLocaleDateString()}
                      </th>
                      <td className="px-6 py-4">
                        {categoryText[client?.contractType]}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          {temporalityText[client?.temporality]}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                          onClick={() => {
                            viewContract(client?._id);
                          }}
                        >
                          {translate("view")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          {isLoading && <Loading />}
          {!isLoading && (
            <Pagination
              key={"paginationContract"}
              data={clientsData}
              page={page}
              pageNumber={pageNumber}
              isLoading={isLoading}
              setPage={setPage}
              totalRecords={totalContracts}
            />
          )}
        </>
      )}
    </div>
  );
};

export default TableContract;
